import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Save, ExternalLink, Plus, Trash2, Star, Pencil, Sparkles,
  MousePointerClick, Type, CheckSquare, ArrowRight, Volume2, Play,
  RefreshCw, Send, Cpu, FileUp, Wrench, Mic, Timer, Globe, FileJson, FileText, Server,
  MessagesSquare, FileCheck2,
} from "lucide-react";
import { PageHeader } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea, Badge } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader } from "../components/ui/dialog";
import AvatarCanvas from "../components/AvatarCanvas";
import { speak, startRecognition, startRemoteRecording } from "../lib/speech";
import { RETURN_TO_VOICE_KEY } from "../lib/kioskFlow";
import api, { formatApiErrorDetail } from "../lib/api";

const stepKeyLabel = (key) => (key === RETURN_TO_VOICE_KEY ? "↩ pagina iniziale" : key);

const selectCls =
  "h-10 w-full border border-line bg-white px-3 text-sm text-ink focus-visible:outline-none focus-visible:border-brand";
const KIND_META = {
  button: { icon: MousePointerClick, label: "Pulsante" },
  text: { icon: Type, label: "Campo testo" },
  checkbox: { icon: CheckSquare, label: "Checkbox" },
  file: { icon: FileUp, label: "Upload documento" },
};
const sanitizeOutcomeValue = (v) =>
  (String(v).trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "") || "empty").slice(0, 40);

export default function ProjectManager() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("avatar");

  useEffect(() => {
    api.get(`/api/projects/${projectId}`).then(({ data }) => setProject(data)).catch(() => {});
  }, [projectId]);

  const agentUrl = `${window.location.origin}/agent/${projectId}`;

  return (
    <div>
      <PageHeader title={project ? project.name : "Progetto"} subtitle="Avatar, flusso e azioni verso API esterne">
        <a href={agentUrl} target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="open-agent-btn">
            <ExternalLink className="h-4 w-4" /> Apri agente
          </Button>
        </a>
      </PageHeader>

      <div className="p-8">
        <div className="mb-6 flex gap-1 self-end border border-line p-1">
          {["avatar", "voce", "flusso", "azioni", "documenti", "mcp", "agente", "conversazioni"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-4 py-2 text-sm font-semibold ${t === "mcp" ? "" : "capitalize"} ${
                tab === t ? "bg-brand text-white" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {t === "mcp" ? "MCP" : t}
            </button>
          ))}
        </div>

        {tab === "avatar" && <AvatarTab projectId={projectId} />}
        {tab === "voce" && <VoiceTab projectId={projectId} />}
        {tab === "flusso" && <FlowTab projectId={projectId} />}
        {tab === "azioni" && <ActionsTab projectId={projectId} />}
        {tab === "documenti" && <DocumentsTab projectId={projectId} />}
        {tab === "mcp" && <McpTab projectId={projectId} />}
        {tab === "agente" && <AgentTab projectId={projectId} />}
        {tab === "conversazioni" && <ConversationsTab projectId={projectId} />}
      </div>
    </div>
  );
}

const SPEECH_PROVIDERS = [
  { id: "openai", label: "OpenAI" },
  { id: "elevenlabs", label: "ElevenLabs" },
  { id: "azure", label: "Azure Speech" },
];
const ENGINE_LABEL = { browser: "Browser (Web Speech)", ...Object.fromEntries(SPEECH_PROVIDERS.map((p) => [p.id, p.label])) };

function VoiceTab({ projectId }) {
  const [rows, setRows] = useState(null); // GET /api/projects/{id}/speech
  const [drafts, setDrafts] = useState({}); // `${kind}:${provider}` -> {api_key, region, voice_id, enabled, priority}
  const [saving, setSaving] = useState({});
  const [testingTts, setTestingTts] = useState(false);
  const [listeningStt, setListeningStt] = useState(false);
  const [sttResult, setSttResult] = useState("");
  const [voices, setVoices] = useState({}); // provider -> [{id, label}]
  const [syncingVoices, setSyncingVoices] = useState({}); // provider -> bool
  const recRef = useRef(null);

  const load = () =>
    api.get(`/api/projects/${projectId}/speech`).then(({ data }) => {
      setRows(data);
      const d = {};
      data.forEach((r) => {
        d[`${r.kind}:${r.provider}`] = {
          api_key: "", // vuoto = non toccare la chiave già salvata
          region: r.region || "",
          voice_id: r.voice_id || "",
          enabled: r.enabled,
          priority: r.priority,
        };
      });
      setDrafts(d);
    });

  useEffect(() => {
    setRows(null);
    load();
  }, [projectId]); // eslint-disable-line

  const statusFor = (kind, provider) => rows?.find((r) => r.kind === kind && r.provider === provider);
  // Il provider realmente usato dal kiosk per questo kind: il primo, in ordine di
  // priorità, tra quelli attivi e con una chiave risolvibile (progetto o globale) —
  // "browser" se nessuno lo è, esattamente come decide backend/speech_providers.py.
  const activeProviderFor = (kind) =>
    (rows || [])
      .filter((r) => r.kind === kind && r.enabled && r.has_key)
      .sort((a, b) => a.priority - b.priority)[0]?.provider || "browser";
  const setDraft = (kind, provider, patch) => {
    const key = `${kind}:${provider}`;
    setDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  };

  const save = async (kind, provider) => {
    const key = `${kind}:${provider}`;
    const d = drafts[key] || {};
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/api/projects/${projectId}/speech/${kind}/${provider}`, {
        api_key: d.api_key ? d.api_key : null,
        region: d.region || null,
        voice_id: d.voice_id || null,
        enabled: d.enabled ?? true,
        priority: Number(d.priority) || 0,
      });
      toast.success("Provider salvato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const remove = async (kind, provider) => {
    if (!window.confirm("Rimuovere la chiave configurata per questo progetto? Si tornerà alla chiave globale (se presente) o al browser."))
      return;
    await api.delete(`/api/projects/${projectId}/speech/${kind}/${provider}`);
    toast.success("Configurazione rimossa");
    load();
  };

  // Testano il percorso reale (usa la configurazione salvata, non le modifiche non
  // ancora salvate qui sopra): stesso motore/fallback che userebbe il kiosk pubblico.
  const testTts = () => {
    if (typeof window !== "undefined" && !window.speechSynthesis) {
      toast.error("Questo browser non supporta la sintesi vocale (Web Speech API).");
      return;
    }
    setTestingTts(true);
    speak("Questo è un test della voce configurata per questo progetto.", {
      lang: "it-IT",
      useRemote: true,
      projectId,
      onEnd: () => setTestingTts(false),
    });
  };

  const toggleSttTest = () => {
    if (listeningStt) {
      recRef.current?.stop?.();
      setListeningStt(false);
      return;
    }
    setSttResult("");
    const onResult = (t) => {
      setSttResult(t);
      setListeningStt(false);
    };
    const onEnd = () => setListeningStt(false);
    const rec =
      activeProviderFor("stt") === "browser"
        ? startRecognition("it-IT", { onResult, onEnd })
        : startRemoteRecording(projectId, "it-IT", { onResult, onEnd });
    recRef.current = rec;
    if (rec) setListeningStt(true);
    else toast.error("Microfono non disponibile in questo browser.");
  };

  // Elenca le voci disponibili chiamando l'API del provider (usa la chiave/regione
  // appena digitate se presenti, altrimenti quelle già salvate per il progetto).
  const syncVoices = async (provider) => {
    const d = drafts[`tts:${provider}`] || {};
    setSyncingVoices((s) => ({ ...s, [provider]: true }));
    try {
      const { data } = await api.post(`/api/projects/${projectId}/speech/tts/${provider}/voices`, {
        api_key: d.api_key || null,
        region: d.region || null,
      });
      setVoices((v) => ({ ...v, [provider]: data }));
      toast.success(`${data.length} voci trovate`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSyncingVoices((s) => ({ ...s, [provider]: false }));
    }
  };

  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  return (
    <div className="space-y-10" data-testid="speech-tab">
      {["stt", "tts"].map((kind) => (
        <div key={kind}>
          <div className="mb-1 flex items-center gap-2">
            {kind === "stt" ? <Mic className="h-4 w-4 text-brand" /> : <Volume2 className="h-4 w-4 text-brand" />}
            <h3 className="font-display text-lg font-bold text-ink">
              {kind === "stt" ? "Riconoscimento vocale (STT)" : "Sintesi vocale (TTS)"}
            </h3>
          </div>
          <p className="mb-3 text-sm text-slate-500">
            {kind === "stt"
              ? "Il browser (gratis, nessuna chiave) è sempre disponibile: è il motore usato in automatico se nessun provider cloud qui sotto è attivo/configurato."
              : "Il browser (gratis, nessuna chiave, lingua/voce configurabili nella tab Avatar) è sempre disponibile: è il motore usato in automatico se nessun provider cloud qui sotto è attivo/configurato."}
          </p>
          <div className="mb-4 flex flex-wrap items-center gap-3 border border-line bg-slate-50 px-4 py-3">
            <span className="text-sm text-slate-600">
              Motore in uso ora per questo progetto: <strong className="text-ink">{ENGINE_LABEL[activeProviderFor(kind)]}</strong>
            </span>
            {kind === "tts" ? (
              <Button size="sm" variant="outline" onClick={testTts} disabled={testingTts} data-testid="speech-test-tts">
                {testingTts ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Prova
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={toggleSttTest} data-testid="speech-test-stt">
                <Mic className={`h-4 w-4 ${listeningStt ? "animate-pulse text-brand" : ""}`} />
                {listeningStt ? "In ascolto… (riclicca per fermare)" : "Prova microfono"}
              </Button>
            )}
            {kind === "stt" && sttResult && (
              <span className="text-sm text-slate-600">
                Trascritto: <strong className="text-ink">"{sttResult}"</strong>
              </span>
            )}
          </div>
          <p className="mb-3 text-xs text-slate-400">
            Il test usa la configurazione già salvata di questo progetto — salva le modifiche prima di provare.
          </p>
          <div className="space-y-3">
            <div className="border border-line bg-slate-50 p-4" data-testid={`speech-row-${kind}-browser`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-ink">Browser (Web Speech)</span>
                <Badge tone={activeProviderFor(kind) === "browser" ? "success" : "default"}>
                  {activeProviderFor(kind) === "browser" ? "In uso ora" : "Fallback finale"}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Sempre disponibile, nessuna chiave da configurare — è il motore predefinito finché
                non attivi un provider cloud qui sotto.
              </p>
            </div>
            {SPEECH_PROVIDERS.map(({ id: provider, label }) => {
              const st = statusFor(kind, provider);
              const key = `${kind}:${provider}`;
              const d = drafts[key] || { api_key: "", region: "", voice_id: "", enabled: true, priority: 0 };
              return (
                <div key={provider} className="border border-line bg-white p-4" data-testid={`speech-row-${kind}-${provider}`}>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-ink">{label}</span>
                      {activeProviderFor(kind) === provider && <Badge tone="success">In uso ora</Badge>}
                      {st?.source === "custom" && <Badge tone="brand">Chiave progetto</Badge>}
                      {st?.source === "global" && <Badge tone="brand">Chiave globale (.env)</Badge>}
                      {st?.source === "none" && <Badge>Non configurato</Badge>}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={d.enabled}
                        onChange={(e) => setDraft(kind, provider, { enabled: e.target.checked })}
                        className="h-4 w-4 accent-brand"
                        data-testid={`speech-enabled-${kind}-${provider}`}
                      />
                      Attivo nel fallback
                    </label>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-1">
                      <Label>Chiave API {st?.has_key ? "(già configurata)" : ""}</Label>
                      <Input
                        type="password"
                        value={d.api_key}
                        onChange={(e) => setDraft(kind, provider, { api_key: e.target.value })}
                        placeholder={st?.has_key ? "•••••••• (lascia vuoto per non cambiarla)" : "Incolla la chiave API"}
                        data-testid={`speech-key-${kind}-${provider}`}
                      />
                    </div>
                    {provider === "azure" && (
                      <div className="space-y-1">
                        <Label>Regione Azure</Label>
                        <Input
                          value={d.region}
                          onChange={(e) => setDraft(kind, provider, { region: e.target.value })}
                          placeholder="es. westeurope"
                          data-testid={`speech-region-${kind}-${provider}`}
                        />
                      </div>
                    )}
                    {kind === "tts" && (
                      <div className="space-y-1">
                        <Label>Voce (nome o id)</Label>
                        <div className="flex gap-1.5">
                          <input
                            list={`speech-voices-${provider}`}
                            value={d.voice_id}
                            onChange={(e) => setDraft(kind, provider, { voice_id: e.target.value })}
                            placeholder="vuoto = voce di default — nome o id, va bene entrambi"
                            className="h-10 w-full border border-line bg-white px-3 text-sm text-ink focus-visible:outline-none focus-visible:border-brand"
                            data-testid={`speech-voice-${kind}-${provider}`}
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={() => syncVoices(provider)}
                            disabled={syncingVoices[provider]}
                            title="Sincronizza voci dal provider"
                            data-testid={`speech-sync-voices-${provider}`}
                          >
                            {syncingVoices[provider] ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          </Button>
                        </div>
                        <datalist id={`speech-voices-${provider}`}>
                          {(voices[provider] || []).map((v) => (
                            <option key={v.id} value={v.id}>{v.label}</option>
                          ))}
                        </datalist>
                        {voices[provider] && (
                          <p className="text-xs text-slate-400">{voices[provider].length} voci disponibili</p>
                        )}
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label>Priorità fallback</Label>
                      <Input
                        type="number"
                        value={d.priority}
                        onChange={(e) => setDraft(kind, provider, { priority: e.target.value })}
                        data-testid={`speech-priority-${kind}-${provider}`}
                      />
                      <p className="text-xs text-slate-400">Più basso = tentato per primo</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={() => save(kind, provider)} disabled={saving[key]} data-testid={`speech-save-${kind}-${provider}`}>
                      {saving[key] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
                    </Button>
                    {st?.source === "custom" && (
                      <Button size="sm" variant="outline" onClick={() => remove(kind, provider)} data-testid={`speech-remove-${kind}-${provider}`}>
                        <Trash2 className="h-4 w-4" /> Rimuovi chiave progetto
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function AvatarTab({ projectId }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingModel, setUploadingModel] = useState(false);
  const [testingVoice, setTestingVoice] = useState(false);

  const testVoice = () => {
    if (typeof window !== "undefined" && !window.speechSynthesis) {
      toast.error("Questo browser non supporta la sintesi vocale (Web Speech API).");
      return;
    }
    setTestingVoice(true);
    speak(form.welcome_message, {
      lang: form.tts_lang, voiceName: form.tts_voice, onEnd: () => setTestingVoice(false),
    });
  };

  const load = () => api.get(`/api/projects/${projectId}/avatar`).then(({ data }) => setForm(data));

  const uploadModel = async (fileObj) => {
    if (!fileObj) return;
    if (!fileObj.name.toLowerCase().endsWith(".glb")) {
      toast.error("Carica un file .glb");
      return;
    }
    const fd = new FormData();
    fd.append("file", fileObj);
    setUploadingModel(true);
    try {
      const { data } = await api.post(`/api/projects/${projectId}/avatar/model`, fd);
      setForm(data);
      toast.success("Modello caricato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingModel(false);
    }
  };

  useEffect(() => {
    setForm(null);
    load();
  }, [projectId]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/projects/${projectId}/avatar`, {
        provider: form.provider, model_url: form.model_url || null, background_url: form.background_url || null,
        model_scale: form.model_scale, model_offset_y: form.model_offset_y,
        form_opacity: form.form_opacity, form_text_color: form.form_text_color,
        form_input_color: form.form_input_color, form_button_color: form.form_button_color,
        welcome_message: form.welcome_message, hide_welcome_text: form.hide_welcome_text,
        tts_provider: form.tts_provider, tts_lang: form.tts_lang, tts_voice: form.tts_voice || null,
      });
      toast.success("Avatar salvato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="avatar-tab">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Tipo di avatar</Label>
          <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className={selectCls} data-testid="avatar-provider-select">
            <option value="threejs">Three.js (placeholder animato)</option>
            <option value="readyplayerme">Modello .glb personalizzato</option>
          </select>
        </div>
        {form.provider === "readyplayerme" && (
          <div className="space-y-1.5">
            <Label>Modello 3D (.glb)</Label>
            <label className="flex cursor-pointer items-center gap-2 border border-line bg-white px-3 py-2.5 text-sm text-ink hover:border-brand" data-testid="avatar-model-upload">
              {uploadingModel ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4 text-brand" />}
              {uploadingModel ? "Caricamento…" : "Carica file .glb"}
              <input type="file" accept=".glb" className="hidden" disabled={uploadingModel} onChange={(e) => uploadModel(e.target.files?.[0])} />
            </label>
            <Input value={form.model_url || ""} onChange={(e) => setForm({ ...form, model_url: e.target.value })} placeholder="…oppure incolla l'URL di un modello già ospitato altrove" data-testid="avatar-modelurl-input" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Immagine di sfondo (opzionale)</Label>
          <Input value={form.background_url || ""} onChange={(e) => setForm({ ...form, background_url: e.target.value })} placeholder="https://…/sfondo.jpg" data-testid="avatar-background-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Dimensione avatar ({form.model_scale?.toFixed(2)})</Label>
            <input type="range" min="0.3" max="3" step="0.05" value={form.model_scale ?? 1.2} onChange={(e) => setForm({ ...form, model_scale: parseFloat(e.target.value) })} className="w-full accent-brand" data-testid="avatar-scale-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Posizione verticale ({form.model_offset_y?.toFixed(2)})</Label>
            <input type="range" min="-3" max="1" step="0.05" value={form.model_offset_y ?? -1.4} onChange={(e) => setForm({ ...form, model_offset_y: parseFloat(e.target.value) })} className="w-full accent-brand" data-testid="avatar-offset-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Messaggio di benvenuto</Label>
          <Textarea value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} data-testid="avatar-welcome-input" />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={!!form.hide_welcome_text} onChange={(e) => setForm({ ...form, hide_welcome_text: e.target.checked })} className="h-4 w-4 accent-brand" data-testid="avatar-hide-welcome-input" />
            Nascondi messaggio a schermo (resta comunque letto dalla voce)
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Lingua TTS</Label>
            <Input value={form.tts_lang} onChange={(e) => setForm({ ...form, tts_lang: e.target.value })} placeholder="it-IT" data-testid="avatar-lang-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Voce (opzionale)</Label>
            <Input value={form.tts_voice || ""} onChange={(e) => setForm({ ...form, tts_voice: e.target.value })} placeholder="Nome voce browser" data-testid="avatar-voice-input" />
          </div>
        </div>
        <div className="space-y-3 border-t border-line pt-4">
          <Label>Pannello form (layer 2): trasparenza e colori</Label>
          <div className="space-y-1.5">
            <Label>Opacità sfondo pannello ({Math.round((form.form_opacity ?? 0.55) * 100)}%)</Label>
            <input type="range" min="0" max="1" step="0.05" value={form.form_opacity ?? 0.55} onChange={(e) => setForm({ ...form, form_opacity: parseFloat(e.target.value) })} className="w-full accent-brand" data-testid="avatar-form-opacity-input" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Testo</Label>
              <input type="color" value={form.form_text_color || "#FFFFFF"} onChange={(e) => setForm({ ...form, form_text_color: e.target.value })} className="h-10 w-full cursor-pointer border border-line bg-white" data-testid="avatar-form-text-color-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Campi input</Label>
              <input type="color" value={form.form_input_color || "#1E1E1E"} onChange={(e) => setForm({ ...form, form_input_color: e.target.value })} className="h-10 w-full cursor-pointer border border-line bg-white" data-testid="avatar-form-input-color-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Pulsanti</Label>
              <input type="color" value={form.form_button_color || "#0055FF"} onChange={(e) => setForm({ ...form, form_button_color: e.target.value })} className="h-10 w-full cursor-pointer border border-line bg-white" data-testid="avatar-form-button-color-input" />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} data-testid="avatar-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva avatar
          </Button>
          <Button variant="outline" onClick={testVoice} disabled={testingVoice} data-testid="avatar-test-voice">
            {testingVoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Prova voce
          </Button>
        </div>
      </div>

      <div className="relative border border-line bg-kiosk-bg bg-cover bg-center" style={{ minHeight: 380, backgroundImage: form.background_url ? `url(${form.background_url})` : undefined }}>
        <AvatarCanvas provider={form.provider} modelUrl={form.model_url} speaking={false} modelScale={form.model_scale} modelOffsetY={form.model_offset_y} className="h-[380px] w-full" />
        <div
          className="pointer-events-none absolute bottom-3 right-3 max-w-[55%] space-y-2 border p-3 text-xs backdrop-blur-xl"
          style={{ backgroundColor: `rgba(10,10,10,${form.form_opacity ?? 0.55})`, borderColor: `${form.form_text_color || "#FFFFFF"}20`, color: form.form_text_color || "#FFFFFF" }}
          data-testid="avatar-form-preview"
        >
          <p className="font-semibold" style={{ color: form.form_button_color || "#0055FF" }}>Anteprima pannello</p>
          <div className="h-2 w-full" style={{ backgroundColor: form.form_input_color || "#1E1E1E" }} />
          <div className="h-6 w-2/3" style={{ backgroundColor: form.form_button_color || "#0055FF", opacity: 0.85 }} />
        </div>
      </div>
    </div>
  );
}

// Editor per una lista di varianti di testo letto.
function TtsListEditor({ label, placeholder, values, onChange, testIdPrefix }) {
  const list = values && values.length ? values : [""];
  const update = (i, v) => { const next = [...list]; next[i] = v; onChange(next); };
  const remove = (i) => onChange(list.length > 1 ? list.filter((_, idx) => idx !== i) : [""]);
  const add = () => onChange([...list, ""]);

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="space-y-2">
        {list.map((v, i) => (
          <div key={i} className="flex items-start gap-2">
            <Textarea value={v} onChange={(e) => update(i, e.target.value)} placeholder={i === 0 ? placeholder : "Un'altra variante…"} className="flex-1" data-testid={`${testIdPrefix}-${i}`} />
            {list.length > 1 && (
              <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`${testIdPrefix}-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            )}
          </div>
        ))}
      </div>
      <Button type="button" size="sm" variant="subtle" onClick={add} data-testid={`${testIdPrefix}-add`}>
        <Plus className="h-3.5 w-3.5" /> Aggiungi variante
      </Button>
    </div>
  );
}

function FlowTab({ projectId }) {
  const [steps, setSteps] = useState(null);
  const [stepDialog, setStepDialog] = useState(null);
  const [elDialog, setElDialog] = useState(null);

  const load = () => api.get(`/api/projects/${projectId}/steps`).then(({ data }) => setSteps(data));

  useEffect(() => {
    setSteps(null);
    load();
  }, [projectId]); // eslint-disable-line

  const stepKeys = useMemo(() => (steps || []).map((s) => s.key), [steps]);

  const delStep = async (id) => {
    if (!window.confirm("Eliminare questo step e i suoi elementi?")) return;
    await api.delete(`/api/projects/steps/${id}`);
    load();
  };
  const delEl = async (id) => {
    await api.delete(`/api/projects/elements/${id}`);
    load();
  };
  const setStart = async (id) => {
    await api.patch(`/api/projects/steps/${id}`, { is_start: true });
    load();
  };

  if (!steps) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  if (steps.length === 0) {
    return (
      <div className="grid-backdrop flex flex-col items-center border border-line bg-white/70 py-16 text-center" data-testid="flow-empty">
        <Sparkles className="h-10 w-10 text-brand" />
        <p className="mt-4 font-display text-lg font-bold text-ink">Nessun flusso configurato</p>
        <p className="mt-1 text-sm text-slate-500">Crea il primo step del tuo agente.</p>
        <Button className="mt-6" onClick={() => setStepDialog({ mode: "create" })} data-testid="add-step-empty-btn">
          <Plus className="h-4 w-4" /> Nuovo step
        </Button>
        {stepDialog && (
          <StepDialog projectId={projectId} data={stepDialog} onClose={() => setStepDialog(null)} onSaved={() => { setStepDialog(null); load(); }} />
        )}
      </div>
    );
  }

  return (
    <div data-testid="flow-tab">
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setStepDialog({ mode: "create" })} data-testid="add-step-btn">
          <Plus className="h-4 w-4" /> Aggiungi step
        </Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {steps.map((s) => (
          <div key={s.id} className="border border-line bg-white" data-testid={`step-card-${s.key}`}>
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <div className="flex items-center gap-2">
                <Badge tone="brand">{s.key}</Badge>
                <span className="font-semibold text-ink">{s.title || "—"}</span>
                {s.is_start && <Badge tone="success"><Star className="mr-1 h-3 w-3" />start</Badge>}
                {s.auto_return_seconds && <Badge><Timer className="mr-1 h-3 w-3" />auto {s.auto_return_seconds}s</Badge>}
              </div>
              <div className="flex items-center gap-1">
                {!s.is_start && (
                  <Button size="icon" variant="ghost" onClick={() => setStart(s.id)} title="Imposta come iniziale" data-testid={`set-start-${s.key}`}>
                    <Star className="h-4 w-4 text-slate-400" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => setStepDialog({ mode: "edit", step: s })} data-testid={`edit-step-${s.key}`}>
                  <Pencil className="h-4 w-4 text-slate-500" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => delStep(s.id)} data-testid={`delete-step-${s.key}`}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
            <div className="px-4 py-3">
              {s.tts?.length > 0 && (
                <p className="mb-3 flex items-start gap-2 text-xs text-slate-500">
                  <Volume2 className="mt-0.5 h-3.5 w-3.5 text-brand" />
                  {s.tts[0]}
                  {s.tts.length > 1 && <span className="ml-1 text-slate-400">(+{s.tts.length - 1} varianti)</span>}
                </p>
              )}
              <div className="space-y-2">
                {s.elements.map((el) => {
                  const meta = KIND_META[el.kind] || KIND_META.button;
                  const Icon = meta.icon;
                  return (
                    <div key={el.id} className="flex items-center justify-between border border-line px-3 py-2" data-testid={`element-${el.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-brand" />
                          <span className="truncate text-sm font-medium text-ink">{el.label || "(senza testo)"}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-400">
                          {el.kind === "button" && el.next_step_key && (
                            <span className="flex items-center gap-1"><ArrowRight className="h-3 w-3" />{stepKeyLabel(el.next_step_key)}</span>
                          )}
                          {el.kind === "button" && el.button_tts?.length > 0 && (
                            <span className="flex items-center gap-1"><Volume2 className="h-3 w-3" />TTS{el.button_tts.length > 1 ? ` ×${el.button_tts.length}` : ""}</span>
                          )}
                          {el.kind === "button" && el.action && (
                            <span className="flex items-center gap-1 text-brand"><Wrench className="h-3 w-3" />{el.action}</span>
                          )}
                          {el.kind === "button" && el.outcomes && Object.keys(el.outcomes).length > 0 && (
                            <span className="flex items-center gap-1 text-brand"><ArrowRight className="h-3 w-3" />instradamento per esito</span>
                          )}
                          {(el.kind === "text" || el.kind === "checkbox" || el.kind === "file") && (
                            <span>{el.field_key}{el.required ? " · obbligatorio" : ""}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0">
                        <Button size="icon" variant="ghost" onClick={() => setElDialog({ stepId: s.id, element: el })} data-testid={`edit-element-${el.id}`}>
                          <Pencil className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => delEl(el.id)} data-testid={`delete-element-${el.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button size="sm" variant="subtle" className="mt-3 w-full" onClick={() => setElDialog({ stepId: s.id, nextPosition: s.elements.length })} data-testid={`add-element-${s.key}`}>
                <Plus className="h-3.5 w-3.5" /> Aggiungi elemento
              </Button>
            </div>
          </div>
        ))}
      </div>

      {stepDialog && (
        <StepDialog projectId={projectId} data={stepDialog} onClose={() => setStepDialog(null)} onSaved={() => { setStepDialog(null); load(); }} />
      )}
      {elDialog && (
        <ElementDialog projectId={projectId} data={elDialog} stepKeys={stepKeys} onClose={() => setElDialog(null)} onSaved={() => { setElDialog(null); load(); }} />
      )}
    </div>
  );
}

function StepDialog({ projectId, data, onClose, onSaved }) {
  const editing = data.mode === "edit";
  const [form, setForm] = useState(
    editing
      ? { ...data.step, tts: data.step.tts?.length ? data.step.tts : [""], auto_return_seconds: data.step.auto_return_seconds ?? "" }
      : { key: "", title: "", tts: [""], is_start: false, auto_return_seconds: "" }
  );
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const tts = (form.tts || []).map((t) => t.trim()).filter(Boolean);
    const auto_return_seconds = form.auto_return_seconds ? Number(form.auto_return_seconds) : null;
    try {
      if (editing) {
        await api.patch(`/api/projects/steps/${data.step.id}`, { key: form.key, title: form.title, tts, is_start: form.is_start, auto_return_seconds });
      } else {
        await api.post(`/api/projects/${projectId}/steps`, { key: form.key, title: form.title, tts, is_start: form.is_start, auto_return_seconds });
      }
      toast.success("Step salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader title={editing ? "Modifica step" : "Nuovo step"} description="Uno step è una schermata del flusso Typeform." />
        <form onSubmit={save} className="space-y-4" data-testid="step-dialog-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Chiave (univoca)</Label>
              <Input required value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.replace(/\s/g, "_") })} placeholder="benvenuto" data-testid="step-key-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Titolo</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Benvenuto" data-testid="step-title-input" />
            </div>
          </div>
          <TtsListEditor label="Testo letto dall'avatar (TTS)" placeholder="Cosa dice l'avatar entrando in questo step" values={form.tts} onChange={(tts) => setForm({ ...form, tts })} testIdPrefix="step-tts" />
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={!!form.is_start} onChange={(e) => setForm({ ...form, is_start: e.target.checked })} className="h-4 w-4 accent-brand" data-testid="step-isstart-input" />
            Step iniziale del flusso
          </label>
          <div className="space-y-1.5">
            <Label>Torna alla pagina iniziale dopo N secondi (opzionale)</Label>
            <Input type="number" min="1" max="600" value={form.auto_return_seconds} onChange={(e) => setForm({ ...form, auto_return_seconds: e.target.value })} placeholder="vuoto = nessun ritorno automatico" data-testid="step-autoreturn-input" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="step-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva step"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const BASE_OUTCOMES = [
  { key: "success", label: "Successo (2xx)" },
  { key: "client_error", label: "Errore richiesta (4xx)" },
  { key: "server_error", label: "Errore server (5xx)" },
  { key: "network_error", label: "Chiamata fallita (rete/timeout)" },
];

function OutcomeRow({ okey, label, form, setForm, stepKeys, testId }) {
  const conf = form.outcomes?.[okey] || { next_step_key: "", tts: [""] };
  const update = (patch) => setForm({ ...form, outcomes: { ...(form.outcomes || {}), [okey]: { ...conf, ...patch } } });
  const msgCount = (conf.tts || []).filter((t) => t.trim()).length;
  return (
    <details className="group">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50 [&::-webkit-details-marker]:hidden" data-testid={`${testId}-summary`}>
        <span className="text-sm font-medium text-ink">{label}</span>
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-slate-400">
          <ArrowRight className="h-3 w-3" />
          {conf.next_step_key ? stepKeyLabel(conf.next_step_key) : "resta qui"}
          {msgCount > 0 && ` · ${msgCount} msg`}
        </span>
      </summary>
      <div className="space-y-2 border-t border-line bg-slate-50/60 px-3 py-3">
        <select value={conf.next_step_key || ""} onChange={(e) => update({ next_step_key: e.target.value })} className={selectCls} data-testid={`${testId}-next-select`}>
          <option value="">— resta su questo step —</option>
          <option value={RETURN_TO_VOICE_KEY}>↩ Torna alla pagina iniziale (schermata voce)</option>
          {stepKeys.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <TtsListEditor label="Messaggio" values={conf.tts} onChange={(tts) => update({ tts })} testIdPrefix={`${testId}-tts`} />
      </div>
    </details>
  );
}

function ElementDialog({ projectId, data, stepKeys, onClose, onSaved }) {
  const editing = !!data.element;
  const [form, setForm] = useState(
    editing
      ? { ...data.element, button_tts: data.element.button_tts?.length ? data.element.button_tts : [""] }
      : { kind: "button", label: "", button_message: "", button_tts: [""], next_step_key: "", field_key: "", placeholder: "", required: false, action: "", outcomes: {}, position: data.nextPosition ?? 0 }
  );
  const [saving, setSaving] = useState(false);
  const [actions, setActions] = useState([]);
  const [customValues, setCustomValues] = useState(() =>
    Object.keys((editing ? data.element.outcomes : {}) || {})
      .filter((k) => k.startsWith("field_") && k !== "field_true" && k !== "field_false")
      .map((k) => k.slice(6))
  );

  useEffect(() => {
    api.get(`/api/projects/${projectId}/actions`).then(({ data }) => setActions(data)).catch(() => {});
  }, [projectId]);

  const selectedAction = actions.find((a) => a.key === form.action);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const outcomes = {};
      for (const [key, conf] of Object.entries(form.outcomes || {})) {
        outcomes[key] = { next_step_key: conf.next_step_key || null, tts: (conf.tts || []).map((t) => t.trim()).filter(Boolean) };
      }
      const payload = {
        kind: form.kind, label: form.label, position: Number(form.position) || 0,
        button_message: form.button_message || null,
        button_tts: (form.button_tts || []).map((t) => t.trim()).filter(Boolean),
        next_step_key: form.next_step_key || null, field_key: form.field_key || null,
        placeholder: form.placeholder || null, required: !!form.required,
        action: form.action || null, outcomes,
      };
      if (editing) await api.patch(`/api/projects/elements/${data.element.id}`, payload);
      else await api.post(`/api/projects/steps/${data.stepId}/elements`, payload);
      toast.success("Elemento salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader title={editing ? "Modifica elemento" : "Nuovo elemento"} description="Pulsante, campo di testo, checkbox o upload documento." />
        <form onSubmit={save} className="space-y-4" data-testid="element-dialog-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={selectCls} data-testid="element-kind-select" disabled={editing}>
                <option value="button">Pulsante</option>
                <option value="text">Campo di testo</option>
                <option value="checkbox">Checkbox</option>
                <option value="file">Upload documento</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Ordine</Label>
              <Input type="number" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="element-position-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Testo / Etichetta</Label>
            <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={form.kind === "button" ? "Continua" : "Il tuo nome"} data-testid="element-label-input" />
          </div>

          {form.kind === "button" && (
            <>
              <div className="space-y-1.5">
                <Label>Vai allo step (next_step_key)</Label>
                <select value={form.next_step_key || ""} onChange={(e) => setForm({ ...form, next_step_key: e.target.value })} className={selectCls} data-testid="element-next-select">
                  <option value="">— nessuno —</option>
                  <option value={RETURN_TO_VOICE_KEY}>↩ Torna alla pagina iniziale (schermata voce)</option>
                  {stepKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <TtsListEditor label="Testo letto al clic (button_tts)" values={form.button_tts} onChange={(button_tts) => setForm({ ...form, button_tts })} testIdPrefix="element-tts" />
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> Azione</Label>
                <select value={form.action || ""} onChange={(e) => setForm({ ...form, action: e.target.value })} className={selectCls} data-testid="element-action-select">
                  <option value="">— nessuna, solo navigazione —</option>
                  {actions.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
                {actions.length === 0 && (
                  <p className="text-xs text-slate-500">Nessuna azione ancora creata: vai alla tab "Azioni" per collegarne una a un'API esterna.</p>
                )}
              </div>
              {form.action && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Instradamento per esito — sostituisce "Vai allo step" e "Testo letto al clic" sopra
                  </p>
                  <div className="divide-y divide-line border border-line">
                    {BASE_OUTCOMES.map((o) => (
                      <OutcomeRow key={o.key} okey={o.key} label={o.label} form={form} setForm={setForm} stepKeys={stepKeys} testId={`outcome-${o.key}`} />
                    ))}
                    {selectedAction?.outcome_field && selectedAction.outcome_field_type === "boolean" && (
                      <>
                        <OutcomeRow okey="field_true" label={`${selectedAction.outcome_field} = true`} form={form} setForm={setForm} stepKeys={stepKeys} testId="outcome-field_true" />
                        <OutcomeRow okey="field_false" label={`${selectedAction.outcome_field} = false`} form={form} setForm={setForm} stepKeys={stepKeys} testId="outcome-field_false" />
                      </>
                    )}
                  </div>
                  {selectedAction?.outcome_field && selectedAction.outcome_field_type === "string" && (
                    <div className="space-y-2 border border-line p-3">
                      <p className="text-xs text-slate-500">
                        Esiti per valore atteso del campo <code>{selectedAction.outcome_field}</code> nella risposta:
                      </p>
                      {customValues.map((v, i) => {
                        const key = `field_${sanitizeOutcomeValue(v)}`;
                        return (
                          <div key={i} className="space-y-1.5 border border-line">
                            <div className="flex items-center gap-2 p-2">
                              <Input
                                value={v}
                                onChange={(e) => {
                                  const next = [...customValues];
                                  next[i] = e.target.value;
                                  setCustomValues(next);
                                }}
                                placeholder="valore atteso (es. paid)"
                                data-testid={`element-custom-outcome-${i}`}
                              />
                              <Button type="button" size="icon" variant="ghost" onClick={() => setCustomValues(customValues.filter((_, idx) => idx !== i))}>
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                            <OutcomeRow okey={key} label={`= ${v || "…"}`} form={form} setForm={setForm} stepKeys={stepKeys} testId={`outcome-${key}`} />
                          </div>
                        );
                      })}
                      <Button type="button" size="sm" variant="subtle" onClick={() => setCustomValues([...customValues, ""])} data-testid="element-add-custom-outcome">
                        <Plus className="h-3.5 w-3.5" /> Aggiungi valore atteso
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {(form.kind === "text" || form.kind === "checkbox" || form.kind === "file") && (
            <>
              <div className="space-y-1.5">
                <Label>Nome variabile (field_key)</Label>
                <Input value={form.field_key || ""} onChange={(e) => setForm({ ...form, field_key: e.target.value })} placeholder={form.kind === "file" ? "documento" : "nome"} data-testid="element-fieldkey-input" />
              </div>
              {(form.kind === "text" || form.kind === "file") && (
                <div className="space-y-1.5">
                  <Label>{form.kind === "file" ? "Testo suggerimento" : "Placeholder"}</Label>
                  <Input value={form.placeholder || ""} onChange={(e) => setForm({ ...form, placeholder: e.target.value })} data-testid="element-placeholder-input" />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={!!form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} className="h-4 w-4 accent-brand" data-testid="element-required-input" />
                Obbligatorio
              </label>
            </>
          )}

          <Button type="submit" className="w-full" disabled={saving} data-testid="element-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva elemento"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Azioni ----------
const AUTH_TYPE_LABEL = { none: "Nessuna", api_key_header: "API key (header)", bearer: "Bearer token", basic: "Basic auth" };
const PARAM_IN_LABEL = { path: "URL (path)", query: "Query string", header: "Header", body: "Body" };

function emptyAction() {
  return {
    key: "", label: "", description: "", method: "GET", url_template: "",
    headers: {}, auth_type: "none", auth_config: {}, params: [], body_type: "none",
    timeout_seconds: 10, outcome_field: "", outcome_field_type: "",
  };
}

function ActionsTab({ projectId }) {
  const [list, setList] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:"create"|"edit", action?}

  const load = () => api.get(`/api/projects/${projectId}/actions`).then(({ data }) => setList(data));

  useEffect(() => {
    setList(null);
    load();
  }, [projectId]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa azione? Gli elementi del flusso che la usano smetteranno di funzionare.")) return;
    await api.delete(`/api/projects/actions/${id}`);
    load();
  };

  if (!list) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  return (
    <div data-testid="actions-tab">
      <div className="mb-4 flex justify-between">
        <p className="text-sm text-slate-500">Azioni verso API esterne, collegabili ai pulsanti del flusso.</p>
        <Button onClick={() => setDialog({ mode: "create" })} data-testid="add-action-btn">
          <Plus className="h-4 w-4" /> Nuova azione
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="grid-backdrop border border-line bg-white/70 py-16 text-center text-slate-500" data-testid="actions-empty">
          Nessuna azione ancora. Crea la prima manualmente o importandola da uno spec OpenAPI.
        </div>
      ) : (
        <div className="divide-y divide-line border border-line bg-white" data-testid="actions-list">
          {list.map((a) => (
            <div key={a.id} className="flex items-center justify-between px-4 py-3" data-testid={`action-row-${a.key}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge tone="brand">{a.method}</Badge>
                  <span className="font-semibold text-ink">{a.label}</span>
                  <span className="text-xs text-slate-400">{a.key}</span>
                </div>
                <p className="mt-1 truncate text-xs text-slate-500">{a.url_template}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" onClick={() => setDialog({ mode: "edit", action: a })} data-testid={`edit-action-${a.key}`}>
                  <Pencil className="h-3.5 w-3.5 text-slate-500" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(a.id)} data-testid={`delete-action-${a.key}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog && (
        <ActionDialog projectId={projectId} data={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      )}
    </div>
  );
}

function ActionDialog({ projectId, data, onClose, onSaved }) {
  const editing = data.mode === "edit";
  const [form, setForm] = useState(editing ? { ...emptyAction(), ...data.action, params: data.action.params || [] } : emptyAction());
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [operations, setOperations] = useState(null);

  const headerRows = Object.entries(form.headers || {});
  const setHeaderRows = (rows) => setForm({ ...form, headers: Object.fromEntries(rows.filter(([k]) => k.trim())) });

  const runImport = async () => {
    setImporting(true);
    setOperations(null);
    try {
      const { data } = await api.post(`/api/projects/${projectId}/actions/import_openapi`, {
        url: importUrl || undefined, spec_text: importText || undefined,
      });
      setOperations(data);
      if (data.length === 0) toast.error("Nessuna operazione trovata nello spec");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const applyOperation = (op) => {
    const params = [
      ...op.parameters
        .filter((p) => ["path", "query", "header"].includes(p.in))
        .map((p) => ({ name: p.name, in: p.in, field_key: p.name, type: p.type === "integer" || p.type === "number" ? "number" : p.type === "boolean" ? "boolean" : "string", required: !!p.required, default: "" })),
      ...op.request_body_properties.map((p) => ({ name: p.name, in: "body", field_key: p.name, type: p.type === "integer" || p.type === "number" ? "number" : p.type === "boolean" ? "boolean" : "string", required: !!p.required, default: "" })),
    ];
    setForm({
      ...form,
      label: form.label || op.summary || op.operation_id || `${op.method} ${op.path}`,
      method: op.method,
      url_template: (op.base_url || "").replace(/\/$/, "") + op.path,
      params,
      body_type: op.request_body_properties.length > 0 ? "json" : form.body_type,
    });
    setImportOpen(false);
    setOperations(null);
    toast.success("Operazione importata: completa auth e field_key prima di salvare");
  };

  const addParam = () => setForm({ ...form, params: [...form.params, { name: "", in: "query", field_key: "", type: "string", required: false, default: "" }] });
  const updateParam = (i, patch) => {
    const next = [...form.params];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, params: next });
  };
  const removeParam = (i) => setForm({ ...form, params: form.params.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      key: form.key, label: form.label, description: form.description || null,
      method: form.method, url_template: form.url_template, headers: form.headers || {},
      auth_type: form.auth_type, auth_config: form.auth_config || {},
      params: form.params, body_type: form.body_type,
      timeout_seconds: Number(form.timeout_seconds) || 10,
      outcome_field: form.outcome_field || null,
      outcome_field_type: form.outcome_field_type || null,
    };
    try {
      if (editing) await api.patch(`/api/projects/actions/${data.action.id}`, payload);
      else await api.post(`/api/projects/${projectId}/actions`, payload);
      toast.success("Azione salvata");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader title={editing ? "Modifica azione" : "Nuova azione"} description="Endpoint esterno, autenticazione e mapping dei campi del form." />

        {!editing && (
          <div className="mb-4 border border-line">
            <button type="button" onClick={() => setImportOpen(!importOpen)} className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-ink hover:bg-slate-50" data-testid="action-import-toggle">
              <FileJson className="h-4 w-4 text-brand" /> Importa da OpenAPI (opzionale)
            </button>
            {importOpen && (
              <div className="space-y-2 border-t border-line bg-slate-50/60 p-3">
                <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://.../openapi.json" data-testid="action-import-url" />
                <p className="text-center text-xs text-slate-400">oppure</p>
                <Textarea value={importText} onChange={(e) => setImportText(e.target.value)} placeholder="Incolla lo spec OpenAPI (JSON o YAML)" data-testid="action-import-text" />
                <Button type="button" size="sm" onClick={runImport} disabled={importing || (!importUrl && !importText)} data-testid="action-import-run">
                  {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />} Leggi spec
                </Button>
                {operations && (
                  <div className="max-h-48 divide-y divide-line overflow-y-auto border border-line bg-white" data-testid="action-import-operations">
                    {operations.map((op, i) => (
                      <button key={i} type="button" onClick={() => applyOperation(op)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-brand-50" data-testid={`action-import-op-${i}`}>
                        <Badge tone="brand">{op.method}</Badge>
                        <span className="truncate">{op.path}</span>
                        {op.summary && <span className="truncate text-slate-400">· {op.summary}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={save} className="space-y-4" data-testid="action-dialog-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Chiave (univoca, usata nel flusso)</Label>
              <Input required disabled={editing} value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.replace(/\s/g, "_") })} placeholder="verifica_ordine" data-testid="action-key-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Etichetta</Label>
              <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Verifica ordine" data-testid="action-label-input" />
            </div>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-4">
            <div className="space-y-1.5">
              <Label>Metodo</Label>
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className={selectCls} data-testid="action-method-select">
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>URL (usa {"{nome}"} per i parametri nel percorso)</Label>
              <Input required value={form.url_template} onChange={(e) => setForm({ ...form, url_template: e.target.value })} placeholder="https://api.esempio.it/ordini/{order_id}" data-testid="action-url-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Autenticazione</Label>
            <select value={form.auth_type} onChange={(e) => setForm({ ...form, auth_type: e.target.value, auth_config: {} })} className={selectCls} data-testid="action-authtype-select">
              {Object.entries(AUTH_TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {form.auth_type === "api_key_header" && (
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.auth_config.header || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, header: e.target.value } })} placeholder="Nome header (es. X-API-Key)" data-testid="action-auth-header-name" />
                <Input value={form.auth_config.value || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, value: e.target.value } })} placeholder="Valore" data-testid="action-auth-header-value" />
              </div>
            )}
            {form.auth_type === "bearer" && (
              <Input value={form.auth_config.token || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, token: e.target.value } })} placeholder="Token" data-testid="action-auth-token" />
            )}
            {form.auth_type === "basic" && (
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.auth_config.username || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, username: e.target.value } })} placeholder="Username" data-testid="action-auth-username" />
                <Input type="password" value={form.auth_config.password || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, password: e.target.value } })} placeholder="Password" data-testid="action-auth-password" />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Header statici aggiuntivi</Label>
            {headerRows.map(([k, v], i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={k} onChange={(e) => { const rows = [...headerRows]; rows[i] = [e.target.value, v]; setHeaderRows(rows); }} placeholder="Header" data-testid={`action-header-name-${i}`} />
                <Input value={v} onChange={(e) => { const rows = [...headerRows]; rows[i] = [k, e.target.value]; setHeaderRows(rows); }} placeholder="Valore" data-testid={`action-header-value-${i}`} />
                <Button type="button" size="icon" variant="ghost" onClick={() => setHeaderRows(headerRows.filter((_, idx) => idx !== i))}>
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="subtle" onClick={() => setHeaderRows([...headerRows, ["", ""]])} data-testid="action-add-header">
              <Plus className="h-3.5 w-3.5" /> Aggiungi header
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>Parametri (mappati dai campi del form)</Label>
            {form.params.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_110px_1fr_90px_auto] items-center gap-2 border border-line p-2">
                <Input value={p.name} onChange={(e) => updateParam(i, { name: e.target.value })} placeholder="nome parametro" data-testid={`action-param-name-${i}`} />
                <select value={p.in} onChange={(e) => updateParam(i, { in: e.target.value })} className={selectCls} data-testid={`action-param-in-${i}`}>
                  {Object.entries(PARAM_IN_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <Input value={p.field_key} onChange={(e) => updateParam(i, { field_key: e.target.value })} placeholder="field_key dello step" data-testid={`action-param-fieldkey-${i}`} />
                <select value={p.type} onChange={(e) => updateParam(i, { type: e.target.value })} className={selectCls} data-testid={`action-param-type-${i}`}>
                  <option value="string">Testo</option>
                  <option value="number">Numero</option>
                  <option value="boolean">Booleano</option>
                </select>
                <Button type="button" size="icon" variant="ghost" onClick={() => removeParam(i)}>
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="subtle" onClick={addParam} data-testid="action-add-param">
              <Plus className="h-3.5 w-3.5" /> Aggiungi parametro
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tipo body (se ci sono parametri "Body")</Label>
              <select value={form.body_type} onChange={(e) => setForm({ ...form, body_type: e.target.value })} className={selectCls} data-testid="action-bodytype-select">
                <option value="none">Nessuno</option>
                <option value="json">JSON</option>
                <option value="form">Form (x-www-form-urlencoded)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Timeout (secondi)</Label>
              <Input type="number" min="1" max="60" value={form.timeout_seconds} onChange={(e) => setForm({ ...form, timeout_seconds: e.target.value })} data-testid="action-timeout-input" />
            </div>
          </div>

          <div className="space-y-1.5 border-t border-line pt-4">
            <Label>Esito dettagliato dalla risposta (opzionale)</Label>
            <p className="text-xs text-slate-500">
              Oltre a successo/errore in base allo status HTTP, puoi instradare in base a un campo della
              risposta JSON (es. <code>data.found</code>).
            </p>
            <div className="grid grid-cols-[1fr_160px] gap-2">
              <Input value={form.outcome_field} onChange={(e) => setForm({ ...form, outcome_field: e.target.value })} placeholder="es. data.found" data-testid="action-outcomefield-input" />
              <select value={form.outcome_field_type} onChange={(e) => setForm({ ...form, outcome_field_type: e.target.value })} className={selectCls} data-testid="action-outcomefieldtype-select">
                <option value="">— tipo campo —</option>
                <option value="boolean">Booleano</option>
                <option value="string">Testo</option>
              </select>
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={saving} data-testid="action-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva azione"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Documenti (RAG) ----------
function DocumentsTab({ projectId }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/api/projects/${projectId}/documents`).then(({ data }) => setDocs(data));

  useEffect(() => {
    setDocs(null);
    load();
  }, [projectId]); // eslint-disable-line

  const upload = async (fileObj) => {
    if (!fileObj) return;
    const fd = new FormData();
    fd.append("file", fileObj);
    setUploading(true);
    try {
      await api.post(`/api/projects/${projectId}/documents`, fd);
      toast.success("Documento indicizzato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare "${d.filename}" dalla knowledge base?`)) return;
    try {
      await api.delete(`/api/projects/${projectId}/documents`, { params: { object_path: d.object_path } });
      toast.success("Documento eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl" data-testid="documents-tab">
      <p className="text-sm text-slate-500">
        Documenti (.txt, .md, .pdf, .docx) usati automaticamente dall'agente per rispondere,
        quando pertinenti — nessuna configurazione aggiuntiva richiesta oltre al caricamento.
      </p>
      <label
        className="mt-4 flex cursor-pointer items-center gap-2 border border-line bg-white px-3 py-2.5 text-sm text-ink hover:border-brand"
        data-testid="document-upload"
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4 text-brand" />}
        {uploading ? "Caricamento e indicizzazione…" : "Carica documento"}
        <input
          type="file" accept=".txt,.md,.pdf,.docx" className="hidden" disabled={uploading}
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
        />
      </label>

      {docs === null ? (
        <Loader2 className="mt-6 h-6 w-6 animate-spin text-brand" />
      ) : docs.length === 0 ? (
        <div className="mt-6 grid-backdrop border border-line bg-white/70 py-12 text-center text-slate-500" data-testid="documents-empty">
          Nessun documento ancora nella knowledge base.
        </div>
      ) : (
        <div className="mt-6 divide-y divide-line border border-line bg-white" data-testid="documents-list">
          {docs.map((d) => (
            <div key={d.object_path} className="flex items-center justify-between px-4 py-3" data-testid={`document-row-${d.object_path}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <FileText className="h-4 w-4 shrink-0 text-brand" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{d.filename}</p>
                  <p className="text-xs text-slate-400">{d.chunks} chunk indicizzati</p>
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`delete-document-${d.object_path}`}>
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- MCP (strumenti dell'agente, non del form) ----------
const AUTH_TYPE_LABEL_MCP = { none: "Nessuna", bearer: "Bearer token", header: "Header personalizzato" };

function emptyMcpServer() {
  return { name: "", url: "", transport: "streamable_http", auth_type: "none", auth_config: {}, enabled_tools: [] };
}

function McpTab({ projectId }) {
  const [list, setList] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:"create"|"edit", server?}

  const load = () => api.get(`/api/projects/${projectId}/mcp-servers`).then(({ data }) => setList(data));

  useEffect(() => {
    setList(null);
    load();
  }, [projectId]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo server MCP? L'agente non potrà più usarne gli strumenti.")) return;
    await api.delete(`/api/projects/mcp-servers/${id}`);
    load();
  };

  if (!list) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  return (
    <div data-testid="mcp-tab">
      <div className="mb-4 flex items-start justify-between gap-4">
        <p className="max-w-xl text-sm text-slate-500">
          Server MCP remoti collegati all'agente: gli strumenti abilitati sono disponibili nella
          chat libera (tool-calling), non nel form del flusso — per quello restano le Azioni.
        </p>
        <Button onClick={() => setDialog({ mode: "create" })} data-testid="add-mcp-server-btn">
          <Plus className="h-4 w-4" /> Nuovo server MCP
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="grid-backdrop border border-line bg-white/70 py-16 text-center text-slate-500" data-testid="mcp-servers-empty">
          Nessun server MCP ancora collegato.
        </div>
      ) : (
        <div className="divide-y divide-line border border-line bg-white" data-testid="mcp-servers-list">
          {list.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-3" data-testid={`mcp-server-row-${s.id}`}>
              <div className="flex min-w-0 items-center gap-2.5">
                <Server className="h-4 w-4 shrink-0 text-brand" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink">{s.name}</span>
                    {!s.is_active && <Badge>Disattivo</Badge>}
                  </div>
                  <p className="truncate text-xs text-slate-400">{s.url} · {(s.enabled_tools || []).length} strumenti abilitati</p>
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" onClick={() => setDialog({ mode: "edit", server: s })} data-testid={`edit-mcp-server-${s.id}`}>
                  <Pencil className="h-3.5 w-3.5 text-slate-500" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-mcp-server-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog && (
        <McpServerDialog projectId={projectId} data={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      )}
    </div>
  );
}

function McpServerDialog({ projectId, data, onClose, onSaved }) {
  const editing = data.mode === "edit";
  const [form, setForm] = useState(editing ? { ...emptyMcpServer(), ...data.server } : emptyMcpServer());
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [tools, setTools] = useState(null); // null finché non si preme "Scopri strumenti"
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [candidates, setCandidates] = useState(null);

  const runImport = async () => {
    setImporting(true);
    setCandidates(null);
    try {
      const { data: found } = await api.post(`/api/projects/${projectId}/mcp-servers/import_json`, { json_text: importText });
      setCandidates(found);
      if (found.length === 0) toast.error("Nessun server trovato nel JSON");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const applyCandidate = (c) => {
    if (!c.supported) return;
    setForm({
      ...form,
      name: form.name || c.name,
      url: c.url,
      transport: c.transport,
      auth_type: c.auth_type,
      auth_config: c.auth_config || {},
    });
    setImportOpen(false);
    setCandidates(null);
    setTools(null);
    toast.success(c.note ? `Server importato — ${c.note}` : "Server importato: scopri gli strumenti prima di salvare");
  };

  const discover = async () => {
    setDiscovering(true);
    setTools(null);
    try {
      const { data: found } = await api.post(`/api/projects/${projectId}/mcp-servers/discover`, {
        url: form.url, transport: form.transport, auth_type: form.auth_type, auth_config: form.auth_config || {},
      });
      setTools(found);
      if (found.length === 0) toast.error("Nessuno strumento trovato su questo server");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDiscovering(false);
    }
  };

  const toggleTool = (name) => {
    const enabled = form.enabled_tools || [];
    setForm({ ...form, enabled_tools: enabled.includes(name) ? enabled.filter((n) => n !== name) : [...enabled, name] });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, url: form.url, transport: form.transport,
      auth_type: form.auth_type, auth_config: form.auth_config || {}, enabled_tools: form.enabled_tools || [],
    };
    try {
      if (editing) await api.patch(`/api/projects/mcp-servers/${data.server.id}`, payload);
      else await api.post(`/api/projects/${projectId}/mcp-servers`, payload);
      toast.success("Server MCP salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // Prima della scoperta in questa sessione del dialog, mostra almeno gli strumenti già
  // abilitati in precedenza (edit) così l'elenco non è mai vuoto subito dopo l'apertura.
  const displayTools = tools !== null ? tools : (form.enabled_tools || []).map((name) => ({ name, description: "" }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader title={editing ? "Modifica server MCP" : "Nuovo server MCP"} description="Endpoint remoto (HTTP Streamable o SSE) e strumenti abilitati per l'agente." />

        {!editing && (
          <div className="mb-4 border border-line">
            <button type="button" onClick={() => setImportOpen(!importOpen)} className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-ink hover:bg-slate-50" data-testid="mcp-import-toggle">
              <FileJson className="h-4 w-4 text-brand" /> Importa da JSON (opzionale)
            </button>
            {importOpen && (
              <div className="space-y-2 border-t border-line bg-slate-50/60 p-3">
                <p className="text-xs text-slate-500">
                  Incolla la config nel formato standard <code>{"{\"mcpServers\": {\"nome\": {\"url\": ...}}}"}</code>
                  {" "}(lo stesso che ti danno di solito per collegare un server agli agenti).
                </p>
                <Textarea value={importText} onChange={(e) => setImportText(e.target.value)} placeholder='{"mcpServers": {"ordini": {"url": "https://.../mcp"}}}' data-testid="mcp-import-text" />
                <Button type="button" size="sm" onClick={runImport} disabled={importing || !importText} data-testid="mcp-import-run">
                  {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileJson className="h-3.5 w-3.5" />} Leggi JSON
                </Button>
                {candidates && (
                  <div className="max-h-48 divide-y divide-line overflow-y-auto border border-line bg-white" data-testid="mcp-import-candidates">
                    {candidates.map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => applyCandidate(c)}
                        disabled={!c.supported}
                        className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-xs ${c.supported ? "hover:bg-brand-50" : "cursor-not-allowed opacity-50"}`}
                        data-testid={`mcp-import-candidate-${i}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-semibold text-ink">{c.name}</span>
                          {c.supported ? <span className="truncate text-slate-400">{c.url}</span> : <Badge>non supportato</Badge>}
                        </span>
                        {!c.supported && <span className="text-red-500">{c.unsupported_reason}</span>}
                        {c.supported && c.note && <span className="text-amber-600">{c.note}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={save} className="space-y-4" data-testid="mcp-server-dialog-form">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Ordini" data-testid="mcp-name-input" />
          </div>
          <div className="grid grid-cols-[1fr_160px] gap-4">
            <div className="space-y-1.5">
              <Label>URL</Label>
              <Input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://.../mcp" data-testid="mcp-url-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Trasporto</Label>
              <select value={form.transport} onChange={(e) => setForm({ ...form, transport: e.target.value })} className={selectCls} data-testid="mcp-transport-select">
                <option value="streamable_http">HTTP Streamable</option>
                <option value="sse">SSE</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Autenticazione</Label>
            <select value={form.auth_type} onChange={(e) => setForm({ ...form, auth_type: e.target.value, auth_config: {} })} className={selectCls} data-testid="mcp-authtype-select">
              {Object.entries(AUTH_TYPE_LABEL_MCP).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {form.auth_type === "bearer" && (
              <Input value={form.auth_config.token || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, token: e.target.value } })} placeholder="Token" data-testid="mcp-auth-token" />
            )}
            {form.auth_type === "header" && (
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.auth_config.header || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, header: e.target.value } })} placeholder="Nome header" data-testid="mcp-auth-header-name" />
                <Input value={form.auth_config.value || ""} onChange={(e) => setForm({ ...form, auth_config: { ...form.auth_config, value: e.target.value } })} placeholder="Valore" data-testid="mcp-auth-header-value" />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Strumenti abilitati</Label>
              <Button type="button" size="sm" variant="outline" onClick={discover} disabled={discovering || !form.url} data-testid="mcp-discover-btn">
                {discovering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Scopri strumenti
              </Button>
            </div>
            {displayTools.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuno strumento scoperto ancora — premi "Scopri strumenti".</p>
            ) : (
              <div className="max-h-56 space-y-2 overflow-y-auto border border-line bg-white p-3" data-testid="mcp-tools-list">
                {displayTools.map((t) => (
                  <label key={t.name} className="flex cursor-pointer items-start gap-2.5" data-testid={`mcp-tool-${t.name}`}>
                    <input type="checkbox" className="mt-0.5" checked={(form.enabled_tools || []).includes(t.name)} onChange={() => toggleTool(t.name)} />
                    <span>
                      <span className="block text-sm font-semibold text-ink">{t.name}</span>
                      {t.description && <span className="block text-xs text-slate-500">{t.description}</span>}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={saving} data-testid="mcp-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva server"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AgentTab({ projectId }) {
  const [cfg, setCfg] = useState(null);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [testReply, setTestReply] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadCfg = () => api.get(`/api/projects/${projectId}/agent`).then(({ data }) => setCfg(data));
  const loadProviders = () => api.get(`/api/projects/agent/providers`).then(({ data }) => setProviders(data));

  useEffect(() => {
    setCfg(null);
    loadCfg();
    loadProviders();
  }, [projectId]); // eslint-disable-line

  const sync = async () => {
    setSyncing(true);
    try {
      await loadProviders();
      toast.success("Modelli sincronizzati");
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/projects/${projectId}/agent`, {
        provider: cfg.provider, model: cfg.model, system_prompt: cfg.system_prompt,
        temperature: Number(cfg.temperature) || 0.4,
      });
      toast.success("Agente salvato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testMsg.trim()) return;
    setTesting(true);
    setTestReply(null);
    try {
      const { data } = await api.post(`/api/projects/${projectId}/agent/test`, { message: testMsg });
      setTestReply(data.reply);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (!cfg) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  const models = providers.find((p) => p.provider === cfg.provider)?.models || [];

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="agent-tab">
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          Le Azioni verso API esterne passano solo dal flusso a pulsanti, mai da qui. La chat usa
          invece automaticamente gli strumenti MCP abilitati (tab "MCP") e i documenti indicizzati
          (tab "Documenti"), quando presenti — nessuna configurazione aggiuntiva richiesta.
        </p>
        {providers.length === 0 && (
          <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="agent-no-providers">
            Nessun provider collegato. Aggiungi le chiavi (OPENAI_API_KEY, ANTHROPIC_API_KEY, GEMINI_API_KEY, OLLAMA_BASE_URL) nel <code>backend/.env</code>, riavvia il backend e premi "Sincronizza modelli".
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Provider</Label>
          <select value={cfg.provider} onChange={(e) => setCfg({ ...cfg, provider: e.target.value, model: "" })} className={selectCls} data-testid="agent-provider-select">
            <option value="">— seleziona —</option>
            {providers.map((p) => <option key={p.provider} value={p.provider}>{p.provider}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Modello</Label>
          <input list="agent-model-list" value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })} placeholder="seleziona o digita un modello" className={selectCls} data-testid="agent-model-input" />
          <datalist id="agent-model-list">{models.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
        <div className="space-y-1.5">
          <Label>System prompt (personalità dell'avatar)</Label>
          <Textarea value={cfg.system_prompt} onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })} data-testid="agent-prompt-input" />
          <div className="border border-line bg-slate-50 p-3 text-xs text-slate-500" data-testid="agent-prompt-vars-hint">
            <p className="font-semibold text-ink">Variabili disponibili nel testo</p>
            <p className="mt-1">
              Scrivi <code className="rounded bg-white px-1 py-0.5">{"{{now}}"}</code> per inserire
              data/ora corrente (sempre UTC) al momento della risposta — derivabile con la stessa
              sintassi di Luxon, es.:
            </p>
            <ul className="mt-1.5 list-disc space-y-1 pl-4">
              <li><code className="rounded bg-white px-1 py-0.5">{"{{now.setZone('Europe/Rome')}}"}</code> — converte al fuso indicato (nome IANA)</li>
              <li><code className="rounded bg-white px-1 py-0.5">{"{{now.plus({days: 7})}}"}</code> — tra 7 giorni (anche <code>hours</code>/<code>weeks</code>/<code>months</code>/<code>years</code>/<code>minutes</code>/<code>seconds</code>)</li>
              <li><code className="rounded bg-white px-1 py-0.5">{"{{now.minus({hours: 3})}}"}</code> — 3 ore fa</li>
              <li><code className="rounded bg-white px-1 py-0.5">{"{{now.setZone('Europe/Rome').toISODate()}}"}</code> — le chiamate si incatenano; <code>toISODate()</code>/<code>toISOTime()</code> restringono all'una o all'altra parte</li>
            </ul>
            <p className="mt-1.5">
              Un placeholder scritto in modo diverso da questi esempi (variabile o metodo non
              riconosciuto) resta così com'è nel testo invece di rompere la chat.
            </p>
          </div>
        </div>
        <div className="w-40 space-y-1.5">
          <Label>Temperature</Label>
          <Input type="number" step="0.1" min="0" max="2" value={cfg.temperature} onChange={(e) => setCfg({ ...cfg, temperature: e.target.value })} data-testid="agent-temp-input" />
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} data-testid="agent-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva agente
          </Button>
          <Button variant="outline" onClick={sync} disabled={syncing} data-testid="agent-sync-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizza modelli
          </Button>
        </div>
      </div>

      <div className="border border-line bg-white p-5">
        <p className="flex items-center gap-2 font-display font-bold text-ink"><Cpu className="h-4 w-4 text-brand" /> Prova l'agente</p>
        <p className="mt-1 text-xs text-slate-500">Salva la configurazione prima di provare.</p>
        <div className="mt-4 flex gap-2">
          <Input value={testMsg} onChange={(e) => setTestMsg(e.target.value)} placeholder="Scrivi un messaggio…" data-testid="agent-test-input" onKeyDown={(e) => e.key === "Enter" && runTest()} />
          <Button onClick={runTest} disabled={testing} data-testid="agent-test-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        {testReply && <div className="mt-4 border-l-2 border-brand bg-brand-50 p-4 text-sm text-ink" data-testid="agent-test-reply">{testReply}</div>}
      </div>
    </div>
  );
}

// ---------- Conversazioni (log delle sessioni del kiosk pubblico) ----------
function ConversationsTab({ projectId }) {
  const [sessions, setSessions] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = () => api.get(`/api/projects/${projectId}/sessions`).then(({ data }) => setSessions(data));

  useEffect(() => {
    setSessions(null);
    setSelectedId(null);
    setDetail(null);
    load();
  }, [projectId]); // eslint-disable-line

  const openSession = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/api/projects/sessions/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingDetail(false);
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

  if (!sessions) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]" data-testid="conversations-tab">
      <div className="border border-line bg-white">
        {sessions.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400" data-testid="conversations-empty">
            Nessuna sessione ancora sul kiosk pubblico.
          </div>
        ) : (
          <div className="max-h-[70vh] divide-y divide-line overflow-y-auto">
            {sessions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => openSession(s.id)}
                className={`flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50 ${selectedId === s.id ? "bg-brand-50" : ""}`}
                data-testid={`conversation-row-${s.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{fmt(s.last_activity_at)}</p>
                  <p className="text-xs text-slate-400">{s.messages_count} messaggi{s.documents_count ? ` · ${s.documents_count} documenti` : ""}</p>
                </div>
                <MessagesSquare className="h-4 w-4 shrink-0 text-slate-300" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border border-line bg-white p-5">
        {!selectedId ? (
          <p className="text-sm text-slate-400">Seleziona una sessione per vedere la trascrizione.</p>
        ) : loadingDetail || !detail ? (
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        ) : (
          <div className="space-y-4" data-testid="conversation-detail">
            <div className="space-y-2.5">
              {detail.messages.length === 0 && <p className="text-sm text-slate-400">Nessun messaggio in questa sessione.</p>}
              {detail.messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-snug ${
                      m.role === "user" ? "bg-brand text-white" : "bg-slate-100 text-ink"
                    }`}
                    data-testid={`conversation-bubble-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
            {detail.documents.length > 0 && (
              <div className="border-t border-line pt-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Documenti caricati</p>
                <div className="space-y-1.5">
                  {detail.documents.map((d) => (
                    <a
                      key={d.id} href={d.url || "#"} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 text-sm text-brand hover:underline"
                      data-testid={`conversation-document-${d.id}`}
                    >
                      <FileCheck2 className="h-3.5 w-3.5" /> {d.field_key || "documento"} ({Math.round(d.size / 1024)} KB)
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
